/**
 * Product Image Upload Component
 *
 * Allows uploading multiple product images with drag & drop,
 * featured image selection, and reordering.
 */

import { useState, useRef, useCallback } from 'react';

interface ProductImageUploadProps {
  storeId: string;
  productId?: string;
  images: string[];
  featuredIndex: number;
  onChange: (images: string[], featuredIndex: number) => void;
  maxImages?: number;
  maxSizeMB?: number;
}

export default function ProductImageUpload({
  storeId,
  productId,
  images,
  featuredIndex,
  onChange,
  maxImages = 10,
  maxSizeMB = 10,
}: ProductImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File): Promise<string | null> => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return null;
    }

    // Validate file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (productId) {
        formData.append('productId', productId);
      }

      const response = await fetch(`/api/ecommerce/stores/${storeId}/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error || 'Upload failed');
      }

      const result = await response.json() as { url: string };
      return result.url;
    } catch (err) {
      console.error('Upload error:', err);
      return null;
    }
  };

  const handleFiles = useCallback(async (files: FileList) => {
    if (images.length >= maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, maxImages - images.length);
    if (filesToUpload.length === 0) return;

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    const newUrls: string[] = [];
    let completed = 0;

    for (const file of filesToUpload) {
      const url = await uploadFile(file);
      if (url) {
        newUrls.push(url);
      }
      completed++;
      setUploadProgress(Math.round((completed / filesToUpload.length) * 100));
    }

    if (newUrls.length > 0) {
      const updatedImages = [...images, ...newUrls];
      onChange(updatedImages, featuredIndex);
    }

    setUploading(false);
    setUploadProgress(0);
  }, [images, maxImages, storeId, productId, featuredIndex, onChange]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    let newFeaturedIndex = featuredIndex;

    if (index === featuredIndex) {
      newFeaturedIndex = 0;
    } else if (index < featuredIndex) {
      newFeaturedIndex = featuredIndex - 1;
    }

    onChange(newImages, Math.min(newFeaturedIndex, newImages.length - 1));
  };

  const handleSetFeatured = (index: number) => {
    onChange(images, index);
  };

  // Drag and drop reordering
  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);

    // Update featured index if needed
    let newFeaturedIndex = featuredIndex;
    if (draggedIndex === featuredIndex) {
      newFeaturedIndex = index;
    } else if (draggedIndex < featuredIndex && index >= featuredIndex) {
      newFeaturedIndex = featuredIndex - 1;
    } else if (draggedIndex > featuredIndex && index <= featuredIndex) {
      newFeaturedIndex = featuredIndex + 1;
    }

    setDraggedIndex(index);
    onChange(newImages, newFeaturedIndex);
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAddUrlImage = () => {
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      const newImages = [...images, url.trim()];
      onChange(newImages, featuredIndex);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl transition-all ${
          dragOver
            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div
          className="p-8 text-center cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <div className="relative w-16 h-16 mb-3">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#14b8a6"
                    strokeWidth="3"
                    strokeDasharray={`${uploadProgress}, 100`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300">
                  {uploadProgress}%
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Uploading...</p>
            </div>
          ) : (
            <>
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-teal-600 dark:text-teal-400">Click to upload</span> or drag and drop
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                PNG, JPG, GIF, WebP up to {maxSizeMB}MB
              </p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-600">
                {images.length}/{maxImages} images
              </p>
            </>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Add URL button */}
      <button
        type="button"
        onClick={handleAddUrlImage}
        className="w-full py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-teal-500 dark:hover:border-teal-500 transition-colors"
      >
        + Add image from URL
      </button>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((url, index) => (
            <div
              key={`${url}-${index}`}
              draggable
              onDragStart={(e) => handleImageDragStart(e, index)}
              onDragOver={(e) => handleImageDragOver(e, index)}
              onDragEnd={handleImageDragEnd}
              className={`relative group aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-move ${
                index === featuredIndex
                  ? 'border-teal-500 ring-2 ring-teal-500/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${draggedIndex === index ? 'opacity-50' : ''}`}
            >
              <img
                src={url}
                alt={`Product image ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"%3E%3Cpath stroke="%239ca3af" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/%3E%3C/svg%3E';
                }}
              />

              {/* Featured Badge */}
              {index === featuredIndex && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-teal-600 text-white text-xs font-medium rounded-full shadow">
                  Featured
                </div>
              )}

              {/* Hover Actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {index !== featuredIndex && (
                  <button
                    type="button"
                    onClick={() => handleSetFeatured(index)}
                    className="p-2 bg-white rounded-full hover:bg-teal-100 transition-colors"
                    title="Set as featured"
                  >
                    <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="p-2 bg-white rounded-full hover:bg-red-100 transition-colors"
                  title="Remove image"
                >
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Drag Handle */}
              <div className="absolute bottom-2 right-2 p-1 bg-white/80 dark:bg-gray-800/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hint */}
      {images.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Drag to reorder. Click the star to set featured image.
        </p>
      )}
    </div>
  );
}
